import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  Stack,
  Modal,
  TextField,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Grid,
  MenuItem,
  FormControl,
  RadioGroup,
  Radio,
  Switch,
  Select,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { esignToolApi } from "../../services/esignApi";
import { esignIcons } from "./icons";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import dayjs from "dayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { apiEndpoint } from "../../urls";
import { clearUploadedDocument } from "../../store/reducers/uploadDocumentSlice";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Loader from "../Loader";
import AddCredits from "./addCredits";
import DOMPurify from "dompurify";
import { useLogger } from "../../hooks/useLogger";
import { toast } from "react-toastify";
import { emailTemplatesApi } from "../../services/emailTemplates";
import AutoReminderIcon from "../../assets/icons/AutoReminderIcon";

const DEFAULT_EMAIL_TEMPLATE_ID = "default_template";

export const formatTimeWithMeridiem = (time24) => {
  if (!time24) return "";

  const [hoursStr, minutes] = time24.split(":");
  let hours = parseInt(hoursStr, 10);

  if (isNaN(hours) || isNaN(parseInt(minutes, 10))) return time24;

  const period = hours >= 12 ? "PM" : "AM";

  // Convert 24h → 12h format
  hours = hours % 12 || 12;

  return `${hours}:${minutes} ${period}`;
};

export default function ReviewAndSend({
  signers,
  reviewAndSend,
  setReviewAndSend,
  saveDraft,
  setSaveDraft,
}) {
  const logEvent = useLogger();
  const dispatch = useDispatch();
  const { profileData } = useSelector((state) => state.profile);
  const { pdfEditortoken } = useSelector((state) => state.pdfEditortoken);
  const { uploadedDocument } = useSelector((state) => state.uploadDocument);
  const { data = {}, status } = useSelector((state) => state.dashboard);
  const [enablePersonalizeMsg, setEnablePersonalizeMsg] = useState(false);
  const [errors, setErrors] = useState({ authPassword: "", personalMsg: "" });
  const [helperText, setHelperText] = useState({
    authPassword: "",
    personalMsg: "",
  });

  const [selectedEsignSigner, setSelectedEsignSigner] = useState("");
  const [signingMode, setSigningMode] = useState("multi_esign");
  const [percentage, setPercentage] = useState(50);
  const [expiredays, setExpireDays] = useState(15);
  const [expiryDate, setExpiryDate] = useState("");
  const [allCraditData, setAllCraditData] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [matchesignname, setMatchEsignName] = useState(false);
  const [successInvite, setSuccessInvite] = useState(false);
  const [authenticationRequired, setAuthenticationRequired] = useState(false);
  const [documentSecurity, setDocumentSecurity] = useState(true);
  const [autoReminderPreview, setAutoReminderPreview] = useState(false);
  const [scheduleInvitePreview, setScheduleInvitePreview] = useState(false);
  const [totalExpiryDays, setTotalExpiryDays] = useState(1);
  const [everyValue, setEveryValue] = useState(1);
  const [everyUnit, setEveryUnit] = useState("Hour");
  const [recurringValue, setRecurringValue] = useState(1);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [addCreditOpen, setAddCreditOpen] = useState(false);
  const [paymentsuccess, setPaymentsuccess] = useState(false);
  const [loader, setLoader] = useState(false);
  const [personalMsg, setPersonalMsg] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [selectedDateTime, setSelectedDateTime] = useState(dayjs());
  const [isReminderSet, setIsReminderSet] = useState(false);
  const [time, setTime] = useState("");
  const [templates, setTemplates] = useState([]);
  const [emailTemplate, setEmailTemplate] = useState("");
  const [emailTemplateId, setEmailTemplateId] = useState("");
  const increaseValue = (setter) =>
    setter((prev) => Math.min(999, Math.max(1, prev + 1)));
  const decreaseValue = (setter) => setter((prev) => Math.max(1, prev - 1));
  dayjs.extend(utc);
  dayjs.extend(timezone);

  useEffect(() => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    setTime(`${hours}:${minutes}`);
  }, []);

  useEffect(() => {
    if (data.status) {
      setAllCraditData(data.credits || {});
    }
  }, [data.status]);

  const handleMatchEsignName = (event) => {
    logEvent("Checked", "Name match checked", "new-esign/Review-Send");
    setMatchEsignName(event.target.checked);
  };

  const handleEnablePersonalizeMsg = (event) => {
    logEvent(
      "Checked",
      "Personalized message checked",
      "new-esign/Review-Send",
    );
    const isChecked = event.target.checked;
    setEnablePersonalizeMsg(isChecked);

    if (!isChecked) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        personalMsg: "",
      }));
      setHelperText((prevHelperText) => ({
        ...prevHelperText,
        personalMsg: "",
      }));
    }
  };

  const handleSendInvite = async () => {
    logEvent("Clicked", "Send Invite Clicked", "new-esign/Review-Send");
    try {
      setLoader(true);
      const data = {
        template_id: emailTemplateId,
        document_id: uploadedDocument?.data?.document_id,
        need_name_match: matchesignname,
        percentage_name_match: percentage,
        days_to_expire: expiredays,
        webhook: `${apiEndpoint.esignBaseUri}/EsignServices/test_webhook`,
        redirect_url: "",
        has_dynamic_document: false,
        dynamic_document_data: "",
        tool_decider: uploadedDocument?.data?.bulk_esign
          ? "bulk_esign"
          : signingMode,
        pdf_uat:
          apiEndpoint.pdfBaseUri === "https://pdf-uat.meon.co.in"
            ? true
            : false,
      };
      const response = await esignToolApi.requestForEsign(data);
      setLoader(false);
      if (response?.success) {
        setReviewAndSend(false);
        setSuccessInvite(true);
        // dispatch(clearUploadedDocument());
        // window.location.replace(`${process.env.PUBLIC_URL}/esign/documents`);
      } else {
        toast.error(
          response?.message || "Failed to send invite. Please try again.",
        );
      }
    } catch (error) {
      setLoader(false);
      console.error("Error fetching data:", error);
    }
  };

  const resetFormData = async () => {
    setTotalExpiryDays(1);
    setEveryValue(1);
    setEveryUnit("Hour");
    // setTime("");
    setEnablePersonalizeMsg(false);
    setPersonalMsg("");
    setErrors((prevErrors) => ({
      ...prevErrors,
      personalMsg: "",
    }));
    setHelperText((prevHelperText) => ({
      ...prevHelperText,
      personalMsg: "",
    }));
  };

  const handleCloseAutoReminderModal = async () => {
    await resetFormData();
    setAutoReminderPreview(false);
  };

  const handleSetAutoReminder = async () => {
    logEvent("Clicked", "Set Auto Reminder", "new-esign/Review-Send");

    if (enablePersonalizeMsg && !personalMsg.trim()) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        personalMsg: "Please enter a personalized message.",
      }));
      setHelperText((prevHelperText) => ({
        ...prevHelperText,
        personalMsg: "Please enter a personalized message.",
      }));
      return;
    }

    try {
      setLoader(true);

      let payload = {
        document_id: uploadedDocument?.data?.document_id,
        days_to_expire: totalExpiryDays,
        everyValue,
        everyUnit,
        time,
        enablePersonalizeMsg,
        personalMsg,
      };
      const response = await esignToolApi.setAutoReminder(payload);

      // Reset form data totalExpiryDays,
      await resetFormData();

      toast.success("Auto reminder has been set successfully.");
      await fetchTemplateAndAutoReminderData();
      // close the modal
      setAutoReminderPreview(false);
    } catch (err) {
      console.log("err", err);
    } finally {
      setLoader(false);
    }
  };

  const handleSetReminder = async () => {
    logEvent("Clicked", "Set Reminder", "new-esign/Review-Send");
    if (selectedDateTime) {
      const formattedDate = dayjs(selectedDateTime).format(
        "YYYY-MM-DD HH:mm:ss.SSSSSS",
      );
      // console.log("Formatted Date:", formattedDate);
      try {
        setLoader(true);
        const data = {
          document_id: uploadedDocument?.data?.document_id,
          need_name_match: matchesignname,
          percentage_name_match: percentage,
          days_to_expire: expiredays,
          webhook: `${apiEndpoint.esignBaseUri}/EsignServices/test_webhook`,
          redirect_url: "",
          has_dynamic_document: false,
          dynamic_document_data: "",
          tool_decider: signingMode,
          is_schedule: true,
          schedule_time: formattedDate,
          pdf_uat:
            apiEndpoint.pdfBaseUri === "https://pdf-uat.meon.co.in"
              ? true
              : false,
        };
        const response = await esignToolApi.scheduleInvite(data);
        // console.log("API Response:", response);
        setLoader(false);

        if (response?.success) {
          // console.log(response)
          setScheduleInvitePreview(false);
          setSuccessInvite(true);
          // dispatch(clearUploadedDocument());
          // window.location.replace(`${process.env.PUBLIC_URL}/esign/documents`);
        }
      } catch (error) {
        setLoader(false);
        console.error("Error fetching data:", error);
      }
      // sendReminder(formattedDate);
    } else {
      console.error("No Date Selected");
    }
  };

  const handlePasswordToggle = () => {
    setShowPassword((prev) => !prev);
  };

  const fetchTemplateAndAutoReminderData = async () => {
    try {
      let payload = {
        document_id: uploadedDocument?.data?.document_id,
      };
      const response =
        await esignToolApi.getTemplateAndAutoReminderData(payload);

      const { template_data, auto_reminder_data } = response || {};

      setEmailTemplateId(template_data?.template_id || "");

      setIsReminderSet(auto_reminder_data?.isReminderSet);
      setTotalExpiryDays(auto_reminder_data?.totalExpiryDays || 1);
      setEveryValue(auto_reminder_data?.everyValue || 1);
      setEveryUnit(auto_reminder_data?.everyUnit || "Hour");
      setShowTimePicker(auto_reminder_data?.everyUnit === "Day");
      if (isReminderSet && auto_reminder_data?.time) {
        setTime(auto_reminder_data.time);
      }
      setEnablePersonalizeMsg(
        auto_reminder_data?.enablePersonalizeMsg || false,
      );
      setPersonalMsg(auto_reminder_data?.personalMsg || "");
    } catch (error) {
      console.log(
        error?.response?.data?.message ||
          "Failed to fetch template and Auto reminder data",
      );
    }
  };

  const fetchAllTemplates = async () => {
    try {
      const response = await emailTemplatesApi.getAllEmailTemplates();
      // setTemplates(response);
      setTemplates(Array.isArray(response) ? response : []);
    } catch (error) {
      console.log(
        error?.response?.data?.message || "Failed to fetch email templates",
      );
    }
  };

  useEffect(() => {
    if (reviewAndSend) {
      setEmailTemplate("");
      fetchTemplateAndAutoReminderData();
      fetchAllTemplates();
    }
  }, [reviewAndSend]);

  // const handleSetDefaultEmailTemplate = async () => {
  //   const defaultTemplate = templates?.find(
  //     (template) => template.template_id === "default_template",
  //   );
  //   if (defaultTemplate) {
  //     setEmailTemplateId(defaultTemplate?.template_id);
  //   }
  // };

  // useEffect(() => {
  //   if (reviewAndSend && templates) {
  //     handleSetDefaultEmailTemplate();
  //   }
  // }, [reviewAndSend, templates]);

  useEffect(() => {
    let today = new Date();
    today.setDate(today.getDate() + expiredays);

    let month = String(today.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    let day = String(today.getDate()).padStart(2, "0");
    let year = today.getFullYear();

    setExpiryDate(`${day}-${month}-${year}`);
  }, [expiredays]);

  const selectedSignerData = signers.find(
    (signer) => signer.id.toString() === selectedEsignSigner.toString(),
  );
  // console.log(selectedSignerData,signers, selectedEsignSigner)
  const selectedSignerName = selectedSignerData
    ? selectedSignerData.name
    : "Unknown Signer";

  const cleanedTemplate =
    emailTemplate &&
    emailTemplate.replace(
      /<a\b([^>]*?)\s*href=(["'])(.*?)\2([^>]*)>/gi,
      '<a$1 href="#"$4>',
    );

  const formattedEmailTemplate = cleanedTemplate
    .replace("{}", selectedSignerName)
    .replace("{}", profileData?.company_name)
    .replace("{}", `<a href="#">${apiEndpoint.esignBaseUri}</a>`)
    .replace("{}", expiryDate);

  useEffect(() => {
    if (signers && signers.length > 0) {
      setSelectedEsignSigner(signers[0].id);
    }
  }, [signers]);

  useEffect(() => {
    // if (emailTemplateId) {
    //   const selectedTemplate = templates.find(
    //     (item) => item.template_id === emailTemplateId,
    //   );
    //   if (selectedTemplate) {
    //     setEmailTemplate(selectedTemplate?.body);
    //   }
    // }
    if (!reviewAndSend) {
      return;
    }
    if (!templates?.length) {
      setEmailTemplate("");
      return;
    }

    const selectedTemplate = templates.find(
      (item) => item.template_id === emailTemplateId,
    );

    if (selectedTemplate) {
      setEmailTemplate(selectedTemplate?.body || "");
      return;
    }

    const defaultTemplate = templates.find(
      (item) => item.template_id === DEFAULT_EMAIL_TEMPLATE_ID,
    );

    if (defaultTemplate) {
      setEmailTemplateId(defaultTemplate.template_id);
      setEmailTemplate(defaultTemplate?.body || "");
      return;
    }

    setEmailTemplate("");
  }, [reviewAndSend, emailTemplateId, templates]);
  // }, [emailTemplateId]);

  const responsiveModalTitleStyles = {
    fontSize: "clamp(20px, 1.4vw, 22px)",
    fontWeight: 600,
    color: "#1D1D1D",
  };

  const responsiveActionButtonStyles = {
    height: 46,
    fontSize: "clamp(14px, 1vw, 16px)",
    borderRadius: "10px",
    textTransform: "none",
    boxShadow: "none",
    fontWeight: "500",
    whiteSpace: "nowrap",
  };
  

  return (
    <>
      <Modal open={reviewAndSend} onClose={() => setReviewAndSend(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(94vw, 920px)",
            maxWidth: "920px",
            minHeight: "min(640px, calc(100vh - 40px))",
            maxHeight: "calc(100vh - 40px)",
            backgroundColor: "#fff",
            boxShadow: 24,
            overflow: "hidden",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { md: "center", xs: "flex-start" },
              flexWrap: "wrap",
              gap: 1.5,
              p: { xs: "18px 20px", md: "20px 30px" },
            }}
          >
            <Typography variant="h6" sx={responsiveModalTitleStyles}>
              Review & Send
            </Typography>
            <Button
              variant="contained"
              startIcon={isReminderSet && <AutoReminderIcon />}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "204px" },
                height: 40,
                color: isReminderSet ? "#00B564" : "#185CB7",
                background: "transparent",
                textTransform: "none",
                fontSize: "clamp(14px, 1vw, 16px)",
                borderRadius: "10px",
                border: "1px solid #D2D2D2",
                boxShadow: "none",
                fontWeight: "500",
                whiteSpace: "nowrap",
                "&:disabled": {
                  background: "#ffffff",
                  color: "#8DB0E2",
                  borderColor: "#8DB0E2",
                },
              }}
              onClick={() => setAutoReminderPreview(true)}
            >
              {isReminderSet ? "Reminder is set" : "Set Auto Reminder"}
            </Button>
          </Box>
          <Divider sx={{ borderWidth: "1px", margin: 0, padding: 0 }} />
          {(() => {
            const aadhaarCredit =
              allCraditData?.aadhaar_credits?.credit_left_integer ?? 0;
            const digitalCredit =
              allCraditData?.digital_credits?.credit_left_integer ?? 0;

            const aadhaarSigners =
              signers?.filter(
                (s) =>
                  (s.esign_type_new || "aadhaar-signature") ===
                  "aadhaar-signature",
              )?.length ?? 0;

            const digitalSigners =
              signers?.filter((s) => s.esign_type_new === "digital-signature")
                ?.length ?? 0;

            const isAadhaarLow =
              aadhaarCredit < 5 || aadhaarCredit < aadhaarSigners;
            const isDigitalLow =
              digitalCredit < 5 || digitalCredit < digitalSigners;

            if (isAadhaarLow || isDigitalLow) {
              return (
                <Box
                  sx={{
                    display: "flex",
                    background: "#FFF3F3",
                    height: "50px",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "16px",
                      fontWeight: "400",
                      color: "#000000",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {isAadhaarLow || isDigitalLow ? (
                      <Typography component="span" sx={{ color: "#EA464A" }}>
                        {digitalCredit < digitalSigners ||
                        aadhaarCredit < aadhaarSigners
                          ? "Isufficient"
                          : "Low"}{" "}
                        Credits -
                      </Typography>
                    ) : (
                      <>Available Credits -</>
                    )}
                    <Typography
                      component="span"
                      sx={{ color: !isAadhaarLow ? "#000000" : "#EA464A" }}
                    >
                      Aadhaar: {aadhaarCredit}
                    </Typography>{" "}
                    /
                    <Typography
                      component="span"
                      sx={{ color: !isDigitalLow ? "#000000" : "#EA464A" }}
                    >
                      Digital: {digitalCredit}
                    </Typography>
                  </Typography>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      borderWidth: "0 0 0 1px !important",
                      margin: 0,
                      borderColor: "#0000001A",
                      height: "80%",
                      alignSelf: "center",
                    }}
                  />
                  <Button
                    variant="contained"
                    sx={{
                      width: "126px",
                      height: 30,
                      color: "#ffffff",
                      background: "#00B564",
                      textTransform: "none",
                      fontSize: "14px",
                      borderRadius: "10px",
                      border: "1px solid #00B564",
                      boxShadow: "none",
                      fontWeight: "500",
                      "&:hover": {
                        background: "#00B564",
                        color: "#ffffff",
                        borderColor: "#00B564",
                      },
                    }}
                    onClick={() => {
                      logEvent(
                        "Clicked",
                        "Add Credit Click",
                        "Review-Send/addCredit",
                      );
                      setAddCreditOpen(true);
                    }}
                  >
                    Add Credits
                  </Button>
                </Box>
              );
            }

            return null;
          })()}

          <Box
            sx={{
              p: { xs: "20px 16px 0", md: "25px 20px 0" },
              flex: 1,
              minHeight: 0,
              overflow: "auto",
            }}
          >
            <Grid
              container
              spacing={{ xs: 2.5, md: 3 }}
              sx={{
                flex: 1,
                minHeight: 0,
              }}
            >
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    mb: 2,
                  }}
                >
                  <Box sx={{ textAlign: "left" }}>
                    <FormControl
                      fullWidth
                      sx={{
                        boxSizing: "border-box",
                        borderRadius: "10px",
                        backgroundColor: "#fff",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          height: 46,
                          fontSize: "16px",
                          fontWeight: "500",
                          "& fieldset": {
                            borderColor: "#C0C0C0",
                          },
                          "&:hover fieldset": {
                            borderColor: "#C0C0C0",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#C0C0C0",
                          },
                        },
                      }}
                    >
                      <Select
                        value={emailTemplateId}
                        onChange={(e) => setEmailTemplateId(e.target.value)}
                        displayEmpty
                      >
                        {templates?.map((item) => (
                          <MenuItem
                            key={item.template_id}
                            value={item.template_id}
                          >
                            {item.template_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  {/* <Box>
                                        <Typography variant="h6" sx={{ fontSize: "18px", fontWeight: "500", color: "#185CB7" }}>
                                            Edit
                                        </Typography>
                                    </Box> */}
                </Box>
                <Box
                  sx={{
                    overflow: "auto",
                    height: "clamp(280px, 42vh, 375px)",
                    p: "0 20px",
                    background: "#F3F3F3",
                    borderRadius: "10px",
                    padding: 2,
                  }}
                >
                  {emailTemplate ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(formattedEmailTemplate),
                      }}
                    />
                  ) : (
                    <Typography variant="body1" color="textSecondary">
                      Loading email template...
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  overflow: "auto",
                  pt: "0 !important",
                  mt: { xs: 0, md: "28px" },
                  height: "clamp(320px, 50vh, 435px)",
                  pr: "3px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "#FFF5ED",
                      padding: "10px 16px",
                      borderRadius: "5px 10px 10px 5px",
                      fontSize: "16px",
                      fontWeight: 400,
                      color: "#1D1D1D",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "5px",
                        height: "100%",
                        backgroundColor: "#F9933D",
                        borderRadius: "5px 0 0 5px",
                      }}
                    />
                    <Typography sx={{ marginLeft: "10px" }}>
                      Document will expire on {expiryDate}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: "25px" }}>
                  <Typography
                    sx={{ color: "#1D1D1D", fontSize: "16px", fontWeight: 500 }}
                  >
                    View as
                  </Typography>
                  <TextField
                    select
                    variant="outlined"
                    fullWidth
                    value={selectedEsignSigner}
                    onChange={(e) => {
                      setSelectedEsignSigner(e.target.value);
                    }}
                    SelectProps={{ native: true }}
                    sx={{
                      mt: 2,
                      boxSizing: "border-box",
                      borderRadius: "10px",
                      backgroundColor: "#F9F9F9",
                      borderColor: "#DFDFDF",
                      borderWidth: "1px",
                      "& .MuiOutlinedInput-root": {
                        borderColor: "#DFDFDF",
                        backgroundColor: "none",
                        "&:hover fieldset": {
                          borderColor: "#DFDFDF",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#DFDFDF",
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        color: "#1D1D1D",
                      },
                    }}
                    InputProps={{
                      sx: {
                        borderRadius: "10px",
                        height: 50,
                        fontSize: "16px",
                        fontWeight: "600",
                        "& input::-ms-reveal, & input::-ms-clear": {
                          display: "none",
                        },
                      },
                    }}
                  >
                    {signers &&
                      signers.map((signer, index) => (
                        <option key={index} value={signer.id}>
                          {signer.name}
                        </option>
                      ))}
                  </TextField>
                  <Typography
                    sx={{
                      color: "#1D1D1D",
                      fontSize: "16px",
                      fontWeight: 500,
                      mt: "22px",
                    }}
                  >
                    Signing Order
                  </Typography>
                  <FormControl component="fieldset" sx={{ mt: "27px" }}>
                    <RadioGroup
                      value={signingMode}
                      onChange={(e) => setSigningMode(e.target.value)}
                    >
                      <FormControlLabel
                        value="multi_esign"
                        control={<Radio sx={{ padding: "0 15px 0 9px" }} />}
                        label={
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#1D1D1D",
                                display: "inline",
                              }}
                            >
                              Sequence
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "12px",
                                fontWeight: 400,
                                color: "#5C5C5C",
                              }}
                            >
                              In Sequential Signing, signers receive the
                              document one by one in a set order.
                            </Typography>
                          </Box>
                        }
                        sx={{ alignItems: "flex-start" }}
                      />
                      <FormControlLabel
                        value="parallel_esign"
                        control={<Radio sx={{ padding: "0 15px 0 9px" }} />}
                        label={
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#1D1D1D",
                                display: "inline",
                              }}
                            >
                              Parallel
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "12px",
                                fontWeight: 400,
                                color: "#5C5C5C",
                              }}
                            >
                              In Parallel Signing, all signers receive and sign
                              the document simultaneously.
                            </Typography>
                          </Box>
                        }
                        sx={{ alignItems: "flex-start", mt: "22px" }}
                      />
                    </RadioGroup>
                  </FormControl>

                  {/* <Box sx={{ display: "flex", alignItems: "center", gap: 2, justifyContent: "space-between", mt: "26px" }}>
                                        <Typography textAlign="center" sx={{ fontSize: 16, fontWeight: 500, }}>
                                            Document Security
                                        </Typography>
                                        <Switch
                                            checked={documentSecurity}
                                            onChange={() => setDocumentSecurity(!documentSecurity)}
                                            sx={{
                                                width: 35,
                                                height: 22,
                                                padding: 0,
                                                "& .MuiSwitch-switchBase": {
                                                    padding: '2px',
                                                    transition: "all 0.3s ease",
                                                    "&.Mui-checked": {
                                                        transform: "translateX(13px)",
                                                        color: "#fff",
                                                        "& + .MuiSwitch-track": {
                                                            backgroundColor: "#4caf50",
                                                            opacity: 1,
                                                        },
                                                    },
                                                },
                                                "& .MuiSwitch-thumb": {
                                                    width: 18,
                                                    height: 18,
                                                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                                },
                                                "& .MuiSwitch-track": {
                                                    borderRadius: 13,
                                                    backgroundColor: "#ccc",
                                                    opacity: 1,
                                                    transition: "all 0.3s ease",
                                                },
                                            }}
                                        />
                                    </Box> */}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      justifyContent: "space-between",
                      mt: "30px",
                    }}
                  >
                    <Typography
                      textAlign="center"
                      sx={{
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "24px",
                        color: "#555555",
                      }}
                    >
                      Do you want to match eSign name ?
                    </Typography>
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={matchesignname}
                            onChange={handleMatchEsignName}
                            sx={{
                              color: "#C0C0C0",
                              "&.Mui-checked": { color: "#185CB7" },
                              "& .MuiSvgIcon-root": {
                                fontSize: 20,
                              },
                              padding: "0",
                            }}
                          />
                        }
                        sx={{
                          color: "#595959",
                          fontSize: "5px",
                          display: "flex",
                          alignItems: "center",
                          gap: "3px",
                          margin: "0",
                          "& .MuiTypography-root": {
                            marginLeft: "0",
                            fontSize: "15px",
                          },
                        }}
                      />
                    </Grid>
                  </Box>
                  {matchesignname && (
                    <>
                      <TextField
                        placeholder="Enter percentage*"
                        type="number"
                        variant="outlined"
                        fullWidth
                        value={percentage}
                        onKeyDown={(e) => {
                          if (["e", "E", "+", "-"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          logEvent(
                            "Type",
                            "Name mismatch percentage",
                            "new-esign/Review-Send",
                          );
                          let val = e.target.value;

                          if (val === "") {
                            setPercentage("");
                            return;
                          }

                          // Limit to two decimal places using regex
                          const regex = /^\d{0,3}(\.\d{0,2})?$/;
                          if (!regex.test(val)) return;

                          let numVal = Number(val);
                          if (numVal < 0) numVal = 0;
                          if (numVal > 100) numVal = 100;

                          setPercentage(numVal);
                          setErrors((prevErrors) => ({
                            ...prevErrors,
                            percentage: "",
                          }));
                          setHelperText((prevHelperText) => ({
                            ...prevHelperText,
                            percentage: "",
                          }));
                        }}
                        error={!!errors.percentage}
                        sx={{
                          mt: 2,
                          boxSizing: "border-box",
                          borderRadius: "10px",
                          backgroundColor: "#fff",
                          borderColor: "#C0C0C0",
                          borderWidth: "5px",
                          "& .MuiOutlinedInput-root": {
                            borderColor: errors.percentage
                              ? "#FF6666"
                              : "#C0C0C0",
                            backgroundColor: errors.percentage
                              ? "#FFF1F1"
                              : "none",
                            "&:hover fieldset": {
                              borderColor: errors.percentage
                                ? "#FF6666"
                                : "#C0C0C0",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: errors.percentage
                                ? "#FF6666"
                                : "#C0C0C0",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            color: "#FF6666",
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              sx={{
                                borderLeft: "1px solid #C0C0C0",
                                maxHeight: "46px",
                                paddingLeft: "12px",
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "#000000",
                                  fontSize: "16px",
                                  fontWeight: 500,
                                  transition: "color 0.2s ease-in-out",
                                  textDecoration: "none",
                                }}
                              >
                                %
                              </Typography>
                            </InputAdornment>
                          ),
                          sx: {
                            borderRadius: "10px",
                            height: 46,
                            fontSize: "14px",
                            fontWeight: "400",
                            "& input::-ms-reveal, & input::-ms-clear": {
                              display: "none",
                            },
                          },
                        }}
                      />

                      {!!errors.percentage && (
                        <Typography
                          sx={{
                            color: "#FF6666",
                            fontSize: "12px",
                            mt: 0.5,
                            mb: 0,
                            marginRight: "auto",
                          }}
                        >
                          {helperText.percentage}
                        </Typography>
                      )}
                    </>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      justifyContent: "space-between",
                      mt: "25px",
                      mb: "16px",
                    }}
                  >
                    <Typography
                      textAlign="center"
                      sx={{
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "24px",
                        color: "#555555",
                      }}
                    >
                      Days to expire eSign request
                    </Typography>
                    <TextField
                      type="number"
                      variant="outlined"
                      value={expiredays}
                      onKeyDown={(e) => {
                        if (["e", "E", "+", "-", "."].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        logEvent(
                          "Type",
                          "Write the expire days of esign",
                          "new-esign/Review-Send",
                        );
                        const paste = e.clipboardData.getData("text");
                        if (!/^\d+$/.test(paste)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        logEvent(
                          "Type",
                          "Write the expire days of esign",
                          "new-esign/Review-Send",
                        );
                        const rawValue = e.target.value;

                        // Allow empty string for clearing input
                        if (rawValue === "") {
                          setExpireDays("");
                          return;
                        }

                        // Ensure the value is numeric
                        const cleanedValue = rawValue.replace(/\D/g, "");
                        let val = parseInt(cleanedValue, 10);

                        if (!val || val < 1) val = 1;
                        if (val > 30) val = 30;

                        setExpireDays(val);
                        setErrors((prevErrors) => ({
                          ...prevErrors,
                          expiredays: "",
                        }));
                        setHelperText((prevHelperText) => ({
                          ...prevHelperText,
                          expiredays: "",
                        }));
                      }}
                      error={!!errors.expiredays}
                      sx={{
                        boxSizing: "border-box",
                        borderRadius: "5px",
                        backgroundColor: "#fff",
                        borderColor: "#C0C0C0",
                        borderWidth: "5px",
                        padding: "0",
                        "& .MuiOutlinedInput-root": {
                          borderColor: errors.expiredays
                            ? "#FF6666"
                            : "#C0C0C0",
                          backgroundColor: errors.expiredays
                            ? "#FFF1F1"
                            : "none",
                          padding: 0,
                          "&:hover fieldset": {
                            borderColor: errors.expiredays
                              ? "#FF6666"
                              : "#C0C0C0",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: errors.expiredays
                              ? "#FF6666"
                              : "#C0C0C0",
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          color: "#FF6666",
                        },
                      }}
                      InputProps={{
                        step: 1,
                        sx: {
                          borderRadius: "5px",
                          height: 32,
                          width: 42,
                          fontSize: "14px",
                          fontWeight: "400",
                          padding: 0,
                          "& input": {
                            padding: 0,
                            textAlign: "center",
                          },
                          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                            {
                              display: "none",
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                          "& input::-ms-reveal, & input::-ms-clear": {
                            display: "none",
                          },
                        },
                      }}
                    />

                    {!!errors.expiredays && (
                      <Typography
                        sx={{
                          color: "#FF6666",
                          fontSize: "12px",
                          mt: 0.5,
                          mb: 0,
                          marginRight: "auto",
                        }}
                      >
                        {helperText.expiredays}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ borderWidth: "1px" }} />

          <Box sx={{ p: { xs: "0 20px", md: "0 30px" }, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  ...responsiveActionButtonStyles,
                  width: { xs: "100%", sm: "171px" },
                  color: "#1D1D1D",
                  background: "transparent",
                  border: "1px solid #D2D2D2",
                }}
                onClick={() => setReviewAndSend(false)}
              >
                Cancel
              </Button>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  flex: 1,
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    ...responsiveActionButtonStyles,
                    width: { xs: "100%", sm: "171px" },
                    color: "#185CB7",
                    background: "transparent",
                    border: "1px solid #185CB7",
                    position: "relative",
                    "&:disabled": {
                      background: "#ffffff",
                      color: "#8DB0E2",
                      borderColor: "#8DB0E2",
                    },
                  }}
                  onClick={() => {
                    logEvent(
                      "Click",
                      "Schedule invite clicked",
                      "new-esign/Review-Send",
                    );
                    setScheduleInvitePreview(true);
                  }}
                  disabled
                >
                  Schedule invite
                  <Box
                    sx={{
                      position: "absolute",
                      top: "-13px",
                      background:
                        "linear-gradient(90deg, #FD1573 0%, #FF7309 100%)",
                      width: 82,
                      height: 24,
                      borderRadius: "6px",
                      fontSize: "12px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "#FFFFFF",
                    }}
                  >
                    UPGRADE
                  </Box>
                </Button>
                {(() => {
                  const aadhaarCredit =
                    allCraditData?.aadhaar_credits?.credit_left_integer ?? 0;
                  const digitalCredit =
                    allCraditData?.digital_credits?.credit_left_integer ?? 0;

                  const aadhaarSigners =
                    signers?.filter(
                      (s) =>
                        (s.esign_type_new || "aadhaar-signature") ===
                        "aadhaar-signature",
                    )?.length ?? 0;

                  const digitalSigners =
                    signers?.filter(
                      (s) => s.esign_type_new === "digital-signature",
                    )?.length ?? 0;

                  const isDisabled =
                    aadhaarCredit < aadhaarSigners ||
                    digitalCredit < digitalSigners;
                  return (
                    <Button
                      variant="contained"
                      sx={{
                        ...responsiveActionButtonStyles,
                        width: { xs: "100%", sm: "196px" },
                        color: "#ffffff",
                        background: "#185CB7",
                        border: "1px solid #185CB7",
                        "&:disabled": {
                          background: "#E4E4E4",
                          color: "#ffffff",
                          borderColor: "#E4E4E4",
                        },
                        "&:hover": {
                          background: "#185CB7",
                          color: "#ffffff",
                          borderColor: "#185CB7",
                        },
                      }}
                      onClick={
                        scheduleInvitePreview
                          ? handleSetReminder
                          : handleSendInvite
                      }
                      disabled={isDisabled}
                    >
                      Send invite now
                    </Button>
                  );
                })()}
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal open={autoReminderPreview} onClose={handleCloseAutoReminderModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: {
              xs: "calc(100vw - 24px)",
              sm: "calc(100vw - 48px)",
              md: "580px",
            },
            maxWidth: "580px",
            minHeight: "444px",
            maxHeight: "calc(100vh - 24px)",
            backgroundColor: "#fff",
            boxShadow: 24,
            overflow: "auto",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: { xs: "16px 16px 16px 18px", sm: "1em 1em 1em 2em" },
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: "22px", fontWeight: 600, color: "#1D1D1D" }}
            >
              Set Auto Reminder
            </Typography>
          </Box>
          <Divider sx={{ borderWidth: "1px", margin: 0, padding: 0 }} />
          <Box sx={{ p: { xs: "16px 16px 0", sm: "16px 30px 0" }, flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                flexWrap: { xs: "wrap", sm: "nowrap" },
                rowGap: 1.5,
              }}
            >
              <Typography
                fullWidth
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "start",
                  flex: 1,
                  height: 40,
                  border: "1px solid #DFDFDF",
                  borderTopLeftRadius: "10px",
                  borderBottomLeftRadius: "10px",
                  borderTopRightRadius: { xs: "10px", sm: 0 },
                  borderBottomRightRadius: { xs: "10px", sm: 0 },
                  padding: "20px 0",
                  paddingLeft: "1rem",
                  backgroundColor: "#F9F9F9",
                  minWidth: { xs: "100%", sm: 0 },
                }}
              >
                Set reminder before eSign expiry
                <Typography
                  variant={"span"}
                  sx={{
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#848383",
                    marginLeft: "0.4rem",
                  }}
                >
                  (in days)
                </Typography>
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                  width: { xs: "100%", sm: 97 },
                  height: 40,
                  border: "1px solid #DFDFDF",
                  borderLeft: { xs: "1px solid #DFDFDF", sm: 0 },
                  borderTopRightRadius: "10px",
                  borderBottomRightRadius: "10px",
                  borderTopLeftRadius: { xs: "10px", sm: 0 },
                  borderBottomLeftRadius: { xs: "10px", sm: 0 },
                  padding: "20px 0",
                }}
              >
                <TextField
                  type="number"
                  variant="outlined"
                  value={totalExpiryDays}
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const paste = e.clipboardData.getData("text");
                    if (!/^\d+$/.test(paste) || paste.length > 3) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    let val = e.target.value.replace(/\D/g, "").slice(0, 3);

                    if (val === "") {
                      setTotalExpiryDays("");
                      return;
                    }

                    let numVal = Number(val);
                    if (numVal < 0) numVal = 0;

                    setTotalExpiryDays(numVal);
                  }}
                  sx={{
                    boxSizing: "border-box",
                    padding: "0",
                    borderRadius: "10px 0 0 10px",
                    "& .MuiOutlinedInput-root": {
                      padding: 0,
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                    },
                  }}
                  InputProps={{
                    sx: {
                      borderRadius: "10px 0 0 10px",
                      height: 40,
                      fontSize: "16px",
                      fontWeight: "500",
                      padding: 0,
                      border: "none",
                      backgroundColor: "transparent",
                      "& input": {
                        padding: 0,
                        textAlign: "center",
                      },
                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                        {
                          display: "none",
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      "& input[type=number]": {
                        MozAppearance: "textfield",
                      },
                      "& input::-ms-reveal, & input::-ms-clear": {
                        display: "none",
                      },
                    },
                  }}
                />
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    borderLeft: "1px solid #DFDFDF",
                    height: "40px",
                    padding: "0",
                    width: "31px",
                    backgroundColor: "#F9F9F9",
                    borderRadius: "0 10px 10px 0",
                    borderLeftRadius: "none",
                  }}
                >
                  <IconButton
                    sx={{ padding: "0 7px" }}
                    onClick={() => increaseValue(setTotalExpiryDays)}
                  >
                    <ArrowDropUpIcon fontSize="small" />
                  </IconButton>
                  <Divider
                    sx={{
                      width: "100%",
                      borderColor: "#DFDFDF",
                      borderWidth: "0 0 1px 0 !important",
                    }}
                  />
                  <IconButton
                    sx={{ padding: "0 7px" }}
                    onClick={() => decreaseValue(setTotalExpiryDays)}
                  >
                    <ArrowDropDownIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>

            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: "25px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "stretch", sm: "center" },
                  flexWrap: "wrap",
                  gap: { xs: 1.5, sm: 2.5 },
                }}
              >
                <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
                  Every
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                    width: { xs: "100%", sm: 97 },
                    height: 40,
                    border: "1px solid #DFDFDF",
                    borderRadius: "10px",
                    padding: "20px 0",
                  }}
                >
                  <TextField
                    type="number"
                    variant="outlined"
                    value={everyValue}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onPaste={(e) => {
                      const paste = e.clipboardData.getData("text");
                      if (!/^\d+$/.test(paste) || paste.length > 3) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      let val = e.target.value.replace(/\D/g, "").slice(0, 3);

                      if (val === "") {
                        setEveryValue("");
                        return;
                      }

                      let numVal = Number(val);
                      if (numVal < 0) numVal = 0;

                      setEveryValue(numVal);
                    }}
                    sx={{
                      boxSizing: "border-box",
                      backgroundColor: "#F9F9F9",
                      padding: "0",
                      borderRadius: "10px 0 0 10px",
                      "& .MuiOutlinedInput-root": {
                        padding: 0,
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused fieldset": {
                          border: "none",
                        },
                      },
                    }}
                    InputProps={{
                      sx: {
                        borderRadius: "10px 0 0 10px",
                        height: 40,
                        fontSize: "16px",
                        fontWeight: "500",
                        padding: 0,
                        border: "none",
                        backgroundColor: "transparent",
                        "& input": {
                          padding: 0,
                          textAlign: "center",
                        },
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                          {
                            display: "none",
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                        "& input[type=number]": {
                          MozAppearance: "textfield",
                        },
                        "& input::-ms-reveal, & input::-ms-clear": {
                          display: "none",
                        },
                      },
                    }}
                  />
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      borderLeft: "1px solid #DFDFDF",
                      height: "40px",
                      padding: "0",
                      width: "31px",
                      backgroundColor: "#F9F9F9",
                      borderRadius: "0 10px 10px 0",
                      borderLeftRadius: "none",
                    }}
                  >
                    <IconButton
                      sx={{ padding: "0 7px" }}
                      onClick={() => increaseValue(setEveryValue)}
                    >
                      <ArrowDropUpIcon fontSize="small" />
                    </IconButton>
                    <Divider
                      sx={{
                        width: "100%",
                        borderColor: "#DFDFDF",
                        borderWidth: "0 0 1px 0 !important",
                      }}
                    />
                    <IconButton
                      sx={{ padding: "0 7px" }}
                      onClick={() => decreaseValue(setEveryValue)}
                    >
                      <ArrowDropDownIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Select
                  value={everyUnit}
                  onChange={(e) => {
                    setEveryUnit(e.target.value);
                    setShowTimePicker(e.target.value === "Day");
                  }}
                  sx={{
                    width: { xs: "100%", sm: 117 },
                    height: 40,
                    background: "#F9F9F9",
                    borderRadius: "10px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid #DFDFDF",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid #DFDFDF",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid #DFDFDF",
                    },
                  }}
                >
                  <MenuItem value="Hour">Hour</MenuItem>
                  <MenuItem value="Day">Day</MenuItem>
                </Select>

                {showTimePicker && (
                  <>
                    <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
                      at
                    </Typography>
                    <TextField
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      variant="outlined"
                      sx={{
                        width: { xs: "100%", sm: 135 },
                        height: 40,
                        borderRadius: "10px",
                        background: "#F9F9F9",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "1px solid #DFDFDF",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: "1px solid #DFDFDF",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: "1px solid #DFDFDF",
                          },
                          "& input": {
                            cursor: "pointer",
                            paddingRight: 0,
                          },
                        },
                      }}
                      InputProps={{
                        sx: {
                          borderRadius: "10px",
                          height: 40,
                          fontSize: "16px",
                          fontWeight: "500",
                          padding: 0,
                          border: "none",
                          backgroundColor: "transparent",
                          "& input::-webkit-calendar-picker-indicator": {
                            opacity: 0, // Hide default icon but keep functionality
                            position: "absolute",
                            width: "100%",
                            height: "100%",
                            cursor: "pointer",
                          },
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              sx={{ paddingLeft: 0, paddingRight: "10px" }}
                            >
                              {esignIcons.clockIcon}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </>
                )}
              </Box>

              {/* <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginTop: "30px",
                }}
              >
                <Typography sx={{ fontWeight: "bold" }}>Recurring</Typography>
                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                    width: 97,
                    height: 40,
                    border: "1px solid #DFDFDF",
                    borderRadius: "10px",
                    padding: "20px 0",
                  }}
                >
                  <TextField
                    type="number"
                    variant="outlined"
                    value={recurringValue}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onPaste={(e) => {
                      const paste = e.clipboardData.getData("text");
                      if (!/^\d+$/.test(paste)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      let val = e.target.value;

                      if (val === "") {
                        setRecurringValue("");
                        return;
                      }

                      let numVal = Number(val);
                      if (numVal < 0) numVal = 0;

                      setRecurringValue(numVal);
                    }}
                    sx={{
                      boxSizing: "border-box",
                      backgroundColor: "#F9F9F9",
                      padding: "0",
                      borderRadius: "10px 0 0 10px",
                      "& .MuiOutlinedInput-root": {
                        padding: 0,
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused fieldset": {
                          border: "none",
                        },
                      },
                    }}
                    InputProps={{
                      sx: {
                        borderRadius: "10px 0 0 10px",
                        height: 40,
                        fontSize: "16px",
                        fontWeight: "500",
                        padding: 0,
                        border: "none",
                        backgroundColor: "transparent",
                        "& input": {
                          padding: 0,
                          textAlign: "center",
                        },
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                          {
                            display: "none",
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                        "& input[type=number]": {
                          MozAppearance: "textfield",
                        },
                        "& input::-ms-reveal, & input::-ms-clear": {
                          display: "none",
                        },
                      },
                    }}
                  />
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      borderLeft: "1px solid #DFDFDF",
                      height: "40px",
                      padding: "0",
                      width: "31px",
                      backgroundColor: "#F9F9F9",
                      borderRadius: "0 10px 10px 0",
                      borderLeftRadius: "none",
                    }}
                  >
                    <IconButton
                      sx={{ padding: "0 7px" }}
                      onClick={() => increaseValue(setRecurringValue)}
                    >
                      <ArrowDropUpIcon fontSize="small" />
                    </IconButton>
                    <Divider
                      sx={{
                        width: "100%",
                        borderColor: "#DFDFDF",
                        borderWidth: "0 0 1px 0 !important",
                      }}
                    />
                    <IconButton
                      sx={{ padding: "0 7px" }}
                      onClick={() => decreaseValue(setRecurringValue)}
                    >
                      <ArrowDropDownIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box> */}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: "30px",
                  mb: "25px",
                }}
              >
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enablePersonalizeMsg}
                        onChange={handleEnablePersonalizeMsg}
                        sx={{
                          color: "#C0C0C0",
                          "&.Mui-checked": { color: "#185CB7" },
                          "& .MuiSvgIcon-root": {
                            fontSize: 27,
                          },
                          padding: "0",
                        }}
                      />
                    }
                    sx={{
                      color: "#595959",
                      fontSize: "5px",
                      display: "flex",
                      alignItems: "center",
                      gap: "3px",
                      margin: "0",
                      "& .MuiTypography-root": {
                        marginLeft: "0",
                        fontSize: "15px",
                      },
                    }}
                  />
                </Grid>
                <Typography
                  textAlign="center"
                  sx={{
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: "24px",
                    color: "#5C5C5C",
                  }}
                >
                  Enable personalize message
                </Typography>
              </Box>

              {enablePersonalizeMsg && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      justifyContent: "space-between",
                    }}
                  >
                    <TextField
                      placeholder="Write message here..."
                      type="text"
                      variant="outlined"
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={5}
                      value={personalMsg}
                      onChange={(e) => {
                        const nextValue = e.target.value.slice(0, 200);
                        setPersonalMsg(nextValue);
                        setErrors((prevErrors) => ({
                          ...prevErrors,
                          personalMsg: "",
                        }));
                        setHelperText((prevHelperText) => ({
                          ...prevHelperText,
                          personalMsg: "",
                        }));
                      }}
                      error={!!errors.personalMsg}
                      helperText={helperText.personalMsg}
                      inputProps={{ maxLength: 200 }}
                      sx={{
                        boxSizing: "border-box",
                        borderRadius: "10px",
                        backgroundColor: "#fff",
                        "& .MuiOutlinedInput-root": {
                          borderColor: errors.personalMsg
                            ? "#FF6666"
                            : "#C0C0C0",
                          backgroundColor: errors.personalMsg
                            ? "#FFF1F1"
                            : "none",
                          "&:hover fieldset": {
                            borderColor: errors.personalMsg
                              ? "#FF6666"
                              : "#C0C0C0",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: errors.personalMsg
                              ? "#FF6666"
                              : "#C0C0C0",
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          color: "#FF6666",
                        },
                      }}
                      InputProps={{
                        sx: {
                          mb: 0,
                          borderRadius: "10px",
                          fontSize: "14px",
                          fontWeight: "400",
                          "& textarea": {
                            resize: "none",
                          },
                          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                            {
                              display: "none",
                            },
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                        },
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 400,
                      textAlign: "right",
                      color: "#5C5C5C",
                      marginBottom: "28px",
                      marginTop: "12px",
                    }}
                  >
                    (Max 200 characters allowed)
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              background: "#FFF5ED",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 40,
              px: 2,
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#1D1D1D",
                textAlign: "center",
              }}
            >
              {!showTimePicker
                ? "Your reminder will be sent every hour until the esign period expires."
                : `Your reminder will be sent daily at ${formatTimeWithMeridiem(time)}. Until the eSign period expires.`}
            </Typography>
          </Box>
          <Divider />

          <Box sx={{ p: { xs: "0 16px", sm: "0 30px" }, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                flexWrap: "wrap",
                mt: 3,
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  width: { xs: "100%", sm: "130px" },
                  height: 46,
                  color: "#1D1D1D",
                  background: "transparent",
                  textTransform: "none",
                  fontSize: "16px",
                  borderRadius: "10px",
                  border: "1px solid #D2D2D2",
                  boxShadow: "none",
                  fontWeight: "500",
                }}
                onClick={handleCloseAutoReminderModal}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  width: { xs: "100%", sm: "155px" },
                  height: 46,
                  color: "#ffffff",
                  background: "#185CB7",
                  textTransform: "none",
                  fontSize: "16px",
                  borderRadius: "10px",
                  border: "1px solid #185CB7",
                  boxShadow: "none",
                  fontWeight: "500",
                  "&:disabled": {
                    background: "#E4E4E4",
                    color: "#ffffff",
                    borderColor: "#E4E4E4",
                  },
                }}
                onClick={handleSetAutoReminder}
              >
                Set Reminder
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={scheduleInvitePreview}
        onClose={() => setScheduleInvitePreview(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "534px",
            height: "382px",
            backgroundColor: "#fff",
            boxShadow: 24,
            overflow: "auto",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "1em 1em  1em 2em",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: "22px", fontWeight: 600, color: "#1D1D1D" }}
            >
              Schedule Invite
            </Typography>
          </Box>
          <Divider sx={{ borderWidth: "1px", margin: 0, padding: 0 }} />
          <Box sx={{ p: "16px 30px 0 30px", flexGrow: 1 }}>
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: "25px",
              }}
            >
              <Typography
                sx={{ fontSize: "18px", fontWeight: 600, color: "#1D1D1D" }}
              >
                Date & Time
              </Typography>
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                  height: 56,
                  border: "1px solid #DFDFDF",
                  borderRadius: "10px",
                  padding: "20px 0",
                  marginTop: "20px",
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    sx={{
                      width: "100%",
                      height: 56,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none !important",
                      },
                      "& .Mui-error .MuiOutlinedInput-notchedOutline": {
                        border: "none !important",
                      },
                    }}
                    value={selectedDateTime}
                    onChange={(newValue) => setSelectedDateTime(newValue)}
                    minDate={dayjs()}
                    minTime={dayjs()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          width: "100%",
                          borderRadius: "10px",
                          background: "#F9F9F9",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "none !important",
                            display: "none !important",
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <IconButton>
                              <CalendarTodayIcon />
                            </IconButton>
                          ),
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ mt: "auto" }} />

          <Box sx={{ p: "0 30px", mb: "20px", position: "relative" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: "20px",
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  width: "130px",
                  height: 46,
                  color: "#1D1D1D",
                  background: "transparent",
                  textTransform: "none",
                  fontSize: "16px",
                  borderRadius: "10px",
                  border: "1px solid #D2D2D2",
                  boxShadow: "none",
                  fontWeight: "500",
                }}
                onClick={() => setScheduleInvitePreview(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  width: "171px",
                  height: 46,
                  color: "#ffffff",
                  background: "#185CB7",
                  textTransform: "none",
                  fontSize: "16px",
                  borderRadius: "10px",
                  border: "1px solid #185CB7",
                  boxShadow: "none",
                  fontWeight: "500",
                  "&:disabled": {
                    background: "#E4E4E4",
                    color: "#ffffff",
                    borderColor: "#E4E4E4",
                  },
                }}
                onClick={handleSetReminder}
              >
                Schedule Send
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal open={saveDraft} onClose={() => setSaveDraft(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "520px",
            height: "368px",
            backgroundColor: "#fff",
            boxShadow: 24,
            overflow: "auto",
            borderRadius: "20px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "1em 1em  1em 2em",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: "22px", fontWeight: 600, color: "#1D1D1D" }}
            >
              Exit Now
            </Typography>
          </Box>
          <Divider sx={{ borderWidth: "1px", margin: 0, padding: 0 }} />
          <Box
            sx={{
              p: "1em 2em",
              mb: 3,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "74%",
            }}
          >
            <Box>
              <img
                src={`${process.env.PUBLIC_URL}/images/save_exit.gif`}
                alt="save_exit"
                style={{ height: 83 }}
              />
            </Box>
            <Typography
              variant="p"
              sx={{ fontSize: "18px", fontWeight: 400, color: "#1D1D1D" }}
            >
              Currently changes will be saved and you can access them later from
              your dashboard.
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: "auto",
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  width: "183px",
                  height: 46,
                  color: "#185CB7",
                  background: "transparent",
                  textTransform: "none",
                  fontSize: "16px",
                  borderRadius: "10px",
                  border: "1px solid #D2D2D2",
                  boxShadow: "none",
                  fontWeight: "500",
                }}
                onClick={() => setSaveDraft(false)}
              >
                Continue Editing
              </Button>
              <Button
                variant="contained"
                sx={{
                  width: "183px",
                  height: 46,
                  color: "#ffffff",
                  background: "#185CB7",
                  textTransform: "none",
                  fontSize: "16px",
                  borderRadius: "10px",
                  border: "1px solid #185CB7",
                  boxShadow: "none",
                  fontWeight: "500",
                }}
                onClick={() => dispatch(clearUploadedDocument())}
              >
                Save & Exit
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal open={successInvite}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "580px",
            height: "410px",
            backgroundColor: "#fff",
            boxShadow: 24,
            overflow: "hidden",
            borderRadius: "20px",
          }}
        >
          <Box
            sx={{
              p: "38px",
              mb: 3,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "100%",
            }}
          >
            <Box sx={{ alignSelf: "center" }}>
              <img
                src={`${process.env.PUBLIC_URL}/images/successful_invite.gif`}
                alt="success"
                style={{ height: 123 }}
              />
            </Box>
            {/* <Typography variant="p" sx={{ marginTop: "63px", fontSize: "18px", fontWeight: 600, color: "#1D1D1D", textAlign: "center" }}>Your Aadhar Esign has been Scheduled Successfully!</Typography>
                        <Typography variant="p" sx={{ marginTop: "22px", fontSize: "16px", fontWeight: 400, color: "#5C5C5C", textAlign: "center" }}>Your Aadhaar eSign request has been successfully scheduled. The invite will be sent at: [{scheduleInvitePreview ? dayjs(selectedDateTime).format("YYYY-MM-DD HH:mm:ss.SSSSSS") : dayjs().format("MM-DD-YYYY  &  h:mm A")}]</Typography> */}
            <Typography
              variant="p"
              sx={{
                marginTop: 0,
                fontSize: "18px",
                fontWeight: 600,
                color: "#1D1D1D",
                textAlign: "center",
              }}
            >
              Your eSign request has been Sent Successfully!
            </Typography>
            <Typography
              variant="p"
              sx={{
                marginTop: "22px",
                fontSize: "16px",
                fontWeight: 400,
                color: "#5C5C5C",
                textAlign: "center",
              }}
            >
              The recipient has been notified and can now proceed with the
              signing process.
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: "auto",
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  marginTop: "32px",
                  width: "298px",
                  height: 46,
                  color: "#ffffff",
                  background: "#185CB7",
                  textTransform: "none",
                  fontSize: "16px",
                  borderRadius: "10px",
                  border: "1px solid #185CB7",
                  boxShadow: "none",
                  fontWeight: "500",
                }}
                onClick={() => {
                  dispatch(clearUploadedDocument());
                  window.location.replace(
                    `${process.env.PUBLIC_URL}/esign/documents`,
                  );
                }}
              >
                Back to Documents
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={authenticationRequired}
        onClose={() => setAuthenticationRequired(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "440px",
            height: "299px",
            backgroundColor: "#fff",
            boxShadow: 24,
            overflow: "auto",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "1em 1em  1em 2em",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: "22px", fontWeight: 600, color: "#1D1D1D" }}
            >
              Authentication Required
            </Typography>
          </Box>
          <Divider sx={{ borderWidth: "1px", margin: 0, padding: 0 }} />
          <Box sx={{ p: "15px 30px 0 30px", flexGrow: 1 }}>
            <Box style={{ display: "flex", flexDirection: "column" }}>
              <Typography
                variant="p"
                sx={{ fontSize: "14px", fontWeight: 400, color: "#616161" }}
              >
                Please enter your credentials to send the invite.
              </Typography>
              <Typography
                variant="p"
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#1D1D1D",
                  mt: "15px",
                }}
              >
                Name: {profileData?.username}
              </Typography>
              <TextField
                placeholder="password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                value={authPassword}
                onChange={(e) => {
                  setAuthPassword(e.target.value);
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    authPassword: "",
                  }));
                  setHelperText((prevHelperText) => ({
                    ...prevHelperText,
                    authPassword: "",
                  }));
                }}
                error={!!errors.authPassword}
                sx={{
                  boxSizing: "border-box",
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  borderColor: "#C0C0C0",
                  borderWidth: "5px",
                  "& .MuiOutlinedInput-root": {
                    borderColor: errors.authPassword ? "#FF6666" : "#C0C0C0",
                    backgroundColor: errors.authPassword ? "#FFF1F1" : "none",
                    "&:hover fieldset": {
                      borderColor: errors.authPassword ? "#FF6666" : "#C0C0C0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: errors.authPassword ? "#FF6666" : "#C0C0C0",
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    color: "#FF6666",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        onClick={handlePasswordToggle}
                        sx={{
                          cursor: "pointer",
                          color: "#185CB7",
                          fontSize: "15px",
                          fontWeight: 600,
                          transition: "color 0.2s ease-in-out",
                          textDecoration: "none",
                        }}
                      >
                        {showPassword ? "Hide" : "Show"}
                      </Typography>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: "10px",
                    height: 46,
                    fontSize: "13px",
                    fontWeight: "400",
                    mt: "15px",
                    "& input::-ms-reveal, & input::-ms-clear": {
                      display: "none",
                    },
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                      {
                        display: "none",
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                    "& input[type=number]": {
                      MozAppearance: "textfield",
                    },
                  },
                }}
              />
              {!!errors.authPassword && (
                <Typography
                  sx={{
                    color: "#FF6666",
                    fontSize: "10px",
                    mt: 0.5,
                    mb: 0,
                    marginRight: "auto",
                  }}
                >
                  {helperText.authPassword}
                </Typography>
              )}
            </Box>
          </Box>
          <Divider sx={{ mt: "auto" }} />

          <Box sx={{ p: "0 30px", mb: "16px", position: "relative" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: "16px",
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  width: "130px",
                  height: 46,
                  color: "#1D1D1D",
                  background: "transparent",
                  textTransform: "none",
                  fontSize: "16px",
                  borderRadius: "10px",
                  border: "1px solid #D2D2D2",
                  boxShadow: "none",
                  fontWeight: "500",
                }}
                onClick={() => setAuthenticationRequired(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  width: "155px",
                  height: 46,
                  color: "#ffffff",
                  background: "#185CB7",
                  textTransform: "none",
                  fontSize: "16px",
                  borderRadius: "10px",
                  border: "1px solid #185CB7",
                  boxShadow: "none",
                  fontWeight: "500",
                  "&:disabled": {
                    background: "#E4E4E4",
                    color: "#ffffff",
                    borderColor: "#E4E4E4",
                  },
                }}
                onClick={
                  scheduleInvitePreview ? handleSetReminder : handleSendInvite
                }
              >
                Proceed
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {loader && <Loader />}
      {(addCreditOpen || paymentsuccess) && (
        <AddCredits
          addCreditOpen={addCreditOpen}
          setAddCreditOpen={setAddCreditOpen}
          loader={loader}
          setLoader={setLoader}
          paymentsuccess={paymentsuccess}
          setPaymentsuccess={setPaymentsuccess}
        />
      )}
    </>
  );
}
